/**
 * Test #9 — the configuration message every assistant surface shows instead
 * of an empty list or a generic error when the assistant is not usable:
 * the panel (as its banner), Assistant Activity, and the model catalog.
 * Reads GET /assistant/status once on mount; renders nothing when configured
 * or when the status itself cannot be read (the surface's own errors still
 * apply).
 */
import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { v2 } from '@/api/v2Client';
import { assistantStatusMessage } from '@/lib/assistantErrors';

const noticeStyle: CSSProperties = {
  border: '1px solid #fecaca',
  background: '#fef2f2',
  color: '#991b1b',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 13,
  marginBottom: 12,
};

export function AssistantConfigNotice({
  testId = 'assistant-config-notice',
}: {
  readonly testId?: string;
}): ReactElement | null {
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    v2.assistant
      .getStatus()
      .then((status) => {
        if (!cancelled) setMessage(assistantStatusMessage(status));
      })
      .catch(() => {
        /* status unknown — the surface's own error handling applies */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  if (message === null) return null;
  return (
    <div role="alert" data-testid={testId} style={noticeStyle}>
      {message}
    </div>
  );
}
